import React, { useEffect } from 'react';
import { __ } from '../../helpers/TranslationService';

import { getRouteSlugs } from '../../helpers/routing';
import { useActions } from '../../hooks/useActions';
import { ICabinInfo, ICruiseReducer } from '../../interfaces';
import { IRequestFormReducer } from '../../interfaces/IRequestForm';
import { CruisePageSectionHeader } from '../../pages/Cruise/components/SectionHeader/SectionHeader';
import { IPageDataReducer } from '../../reducers/pageData';
import { Button } from '../Button/Button';
import { MultiplePricesSwitch } from '../MultiplePricesSwitch/MultiplePricesSwitch';
import RequestFormCabinType from '../RequestForm/RequestFormCabinType';
import RequestFormDates from '../RequestForm/RequestFormDates';
import RequestFormSummary from '../RequestForm/RequestFormSummary';
import RequestFormTravelers from '../RequestForm/RequestFormTravelers';
import { isChildAgeValid } from '../RequestForm/helpers';
import { CruiseInquiryCabinSelection } from './CruiseInquiryCabinSelection';
import { CruiseInquiryFlightSelection } from './CruiseInquiryFlightSelection';
import {
    RequestFormContainer,
    RequestFormContent,
    RequestFormLeftSide,
    RequestFormRightSide,
    Separator,
} from './CruiseInquiryForm.style';
import { CruiseInquiryPhoneCardV2 } from './CruiseInquiryPhoneCardV2';
import { FieldsEnumKeys, useInquiryFormValidation } from './useInquiryFormValidation';

import styled from '@emotion/styled';
import { MarketContext } from '../../context/markets';
import { RequestFormLoader } from '../RequestForm/RequestFormLoader';

interface IRequestForm {
    startHarbour: string;
    pageData: IPageDataReducer;
    cruisePage: ICruiseReducer;
    requestForm: IRequestFormReducer;
    onToogle?: (event: any) => void;
    isSoldOut?: boolean;
    cabin: ICabinInfo;
}

const RequestFormRightSideWrapper = styled.div`
    position: sticky;
    top: 110px; // sticky header height 90px + 20px margin
`;

export const CruiseInquiryForm = (props: IRequestForm) => {
    const {
        setAdultsNum,
        setChildrenNum,
        setSelectedSailIndex,
        setChildAge,
        updateRequestFormFields,
        submitCruiseInquiryForm,
        setAirportCode,
        setChildrenAges,
    } = useActions();

    const { pageData, cruisePage, requestForm, startHarbour, cabin } = props;

    const [isAllInclusiveSelected, setIsAllInclusiveSelected] = React.useState(false);

    const dateOfDeparture = cruisePage?.sails[cruisePage.selectedSailIndex]?.departure;
    const { processedCabins, sails, selectedSailIndex, ratesBySail, operator } = cruisePage;
    const { dualCurrency, defaultCurrency } = pageData.appConfig;
    const { exchangeRate } = pageData.header;
    const currency = dualCurrency || defaultCurrency;

    const { markets } = React.useContext(MarketContext);
    const { isCruise1stAU, isCruiseAway } = markets;
    const isAustralianMarket = isCruiseAway || isCruise1stAU;

    const selectedRates = cruisePage?.rates?.filter(
        (rate) => rate.sail_nid === cruisePage?.sails[cruisePage.selectedSailIndex]?.nid,
    );
    const selectedCabin: ICabinInfo = !cruisePage.loadingRates ? cabin : null;
    const cabinRates = selectedRates?.find(
        (rate) => rate.cabintype_id === selectedCabin?.cabinType.nid,
    );
    const isCheapestPriceAllIn = cabinRates?.cheapest_price.is_all_inclusive;
    const allInPrice = cabinRates?.all_in_price?.per_person_price;

    const { searchresults: searchUrlSlug, allInclusivePage } = getRouteSlugs(
        pageData.brandConfig.slugs,
    );

    const handleAllInclusiveSwitcher = (isAllInclusiveToggled: boolean) => {
        setIsAllInclusiveSelected(isAllInclusiveToggled);
        setAirportCode(null);
    };

    const personalInfoControlsEnum = useInquiryFormValidation({
        validateNow: false,
        isAllInclusiveSelected,
    });

    const validatePersonalForm = () => {
        for (const field in personalInfoControlsEnum) {
            personalInfoControlsEnum[field].control.setDirty(true);
        }

        submitCruiseInquiryForm(isAllInclusiveSelected);
    };

    React.useEffect(() => {
        const rate = selectedRates.find(
            (rate) => rate.cabintype_id === selectedCabin?.cabinType.nid,
        );

        const isAllin =
            (isAllInclusiveSelected && !!rate?.all_in_price?.per_person_price) ||
            !!rate?.cheapest_price?.is_all_inclusive;
        setIsAllInclusiveSelected(isAllin);
    }, [selectedCabin]);

    React.useEffect(() => {
        if (
            selectedCabin &&
            (requestForm.cabinNid === null || requestForm.cabinNid !== selectedCabin.cabinType.nid)
        ) {
            updateRequestFormFields({
                cabinNid: selectedCabin?.cabinType?.nid,
                cruiseNid: cruisePage?.nid,
                sailNid: cruisePage?.sails[cruisePage.selectedSailIndex]?.nid,
                selectedSailIndex: cruisePage?.selectedSailIndex,
            });
        }
    }, [
        selectedCabin,
        cruisePage?.nid,
        cruisePage?.sails,
        cruisePage.selectedSailIndex,
        requestForm.cabinNid,
    ]);

    useEffect(() => {
        setAirportCode(null);
    }, [cruisePage?.nid, cruisePage?.sails, cruisePage.selectedSailIndex]);

    React.useEffect(() => {
        const newChildAges = requestForm.childAges.map((item, index) => {
            const { age, birthday } = item;
            return {
                age,
                birthday,
                index,
                isValid: isChildAgeValid(
                    dateOfDeparture,
                    birthday,
                    cruisePage.nights,
                    cruisePage.company.bookingServiceCode,
                ),
            };
        });

        setChildrenAges(newChildAges);
    }, [dateOfDeparture]);

    return (
        <RequestFormContainer className="requestFormContainer">
            <CruisePageSectionHeader>
                {__('headlines.datesAndPrices', 'dreamlines')}
            </CruisePageSectionHeader>

            <RequestFormContent>
                {cruisePage.loadingRates && <RequestFormLoader />}
                <RequestFormLeftSide>
                    <RequestFormTravelers
                        setChildrenNum={setChildrenNum}
                        setAdultsNum={setAdultsNum}
                        adultsNum={requestForm.adultsNum}
                        dateOfDeparture={dateOfDeparture}
                        cruiseCompanyCode={cruisePage.company.bookingServiceCode}
                        cruiseNights={cruisePage.nights}
                        childrenNum={requestForm.childrenNum}
                        childAges={requestForm.childAges}
                        minAdultAge={requestForm.selectedPax.adult.minAge}
                        isSubmitted={requestForm.isSubmitted}
                        setChildAge={setChildAge}
                    />

                    <RequestFormDates
                        sails={sails}
                        selectedSailIndex={selectedSailIndex}
                        setSelectedSailIndex={setSelectedSailIndex}
                        defaultCurrency={defaultCurrency}
                        currency={currency}
                        exchangeRate={exchangeRate}
                        soldOutBooking={cruisePage.soldOutBooking}
                        passengersNum={(requestForm.passengers || []).length}
                        ratesBySail={ratesBySail}
                    />

                    <CruiseInquiryCabinSelection
                        selectedCabinId={selectedCabin?.cabinType.nid}
                        cabins={processedCabins}
                        exchangeRate={exchangeRate}
                        selectedRates={selectedRates}
                        sails={sails}
                        operator={cruisePage.company.bookingServiceCode}
                    />

                    {allInPrice && (
                        <MultiplePricesSwitch
                            allInclusiveUrl={allInclusivePage}
                            allInclusiveDescription={cruisePage.ship.allInclusiveDescription}
                            isAllInclusiveSelected={isAllInclusiveSelected}
                            onToogle={handleAllInclusiveSwitcher}
                            disabled={isCheapestPriceAllIn}
                        />
                    )}

                    <RequestFormCabinType
                        selectedCabinId={selectedCabin?.cabinType.nid}
                        cabinsObj={processedCabins || {}}
                        onChange={updateRequestFormFields}
                        selectedRates={selectedRates}
                        isAllInclusiveSelected={isAllInclusiveSelected}
                        passengersNum={(requestForm.passengers || []).length}
                        tariffsDescription={cruisePage.operator.tariffsDescription}
                    />

                    {cruisePage.includesFlight && (
                        <CruiseInquiryFlightSelection
                            flightField={
                                personalInfoControlsEnum[FieldsEnumKeys.selectedAirportIndex]
                            }
                            isAllInclusive={isAllInclusiveSelected}
                        />
                    )}

                    {!props.isSoldOut && (
                        <Button fullWidth withArrow onClick={validatePersonalForm}>
                            {__('continue', 'dreamlines')}
                        </Button>
                    )}
                </RequestFormLeftSide>

                <RequestFormRightSide>
                    <RequestFormRightSideWrapper>
                        <RequestFormSummary
                            cruise={cruisePage}
                            requestForm={requestForm}
                            pageData={pageData}
                            selectedCabinId={selectedCabin?.cabinType.nid}
                            selectedSailIndex={selectedSailIndex}
                            cabin={selectedCabin}
                            departure={sails[selectedSailIndex]?.departure}
                            isDualCurrency={dualCurrency !== null}
                            showDirectBookingLabel={false}
                            searchUrlSlug={searchUrlSlug}
                            startHarbour={startHarbour}
                            isAllInclusive={isAllInclusiveSelected}
                            selectedRates={selectedRates}
                        />

                        <Separator />

                        <CruiseInquiryPhoneCardV2
                            phoneNumber={pageData.brandConfig.phoneNumber}
                            officeHours={pageData.header.headerContent.officeHours}
                            companyTitle={cruisePage.company.title}
                            isDreamdeal={operator.isDreamlinesPackage}
                            isAllInclusive={isAllInclusiveSelected}
                            isSoldOut={props.isSoldOut}
                        />
                    </RequestFormRightSideWrapper>
                </RequestFormRightSide>
            </RequestFormContent>
            {isAustralianMarket && (
                <div className="requestFormContainer__disclaimer-text">
                    {__('priceDisclaimer', 'dreamlines')}
                </div>
            )}
        </RequestFormContainer>
    );
};
